/**
 * AcuityGapCharts - Shows PBJ actual vs MDS expected staffing by staff type
 *
 * Three side-by-side area charts (RN, LPN, CNA) showing the weekly gap
 * between what was staffed (PBJ) and what should've been staffed (MDS acuity).
 * Year selector controls all three charts simultaneously.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import api from '../../api/axios';

// ─────────────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────────────

interface WeekData {
  week: string;
  pbj_rn: number;
  mds_rn: number;
  pbj_lpn: number;
  mds_lpn: number;
  pbj_cna: number;
  mds_cna: number;
  days: number;
}

interface AcuityTimelineResponse {
  ccn: string;
  year: number;
  timeline: WeekData[];
}

// ─────────────────────────────────────────────────────────────────────────────
// SINGLE STAFF TYPE CHART
// ─────────────────────────────────────────────────────────────────────────────

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatWeekLabel = (weekStr: string): string => {
  if (!weekStr) return '';
  const [, m, d] = weekStr.split('-');
  const monthIdx = parseInt(m) - 1;
  return `${MONTH_LABELS[monthIdx]} ${parseInt(d)}`;
};

const StaffTypeChart: React.FC<{
  title: string;
  data: { week: string; pbj: number; mds: number; days: number }[];
  height?: number;
  loading?: boolean;
}> = ({ title, data: rawData, height = 220, loading = false }) => {
  const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);

  // The year's first and last weeks are cut off at Jan 1 / Dec 31, so plot every
  // week at full-week scale; otherwise a 2-day week looks like a staffing collapse.
  const data = rawData.map(d => {
    const scale = d.days > 0 ? 7 / d.days : 1;
    return { ...d, pbj: d.pbj * scale, mds: d.mds * scale };
  });

  if (data.length === 0) {
    return (
      <Box sx={{ flex: 1, minWidth: 240 }}>
        <Typography variant="subtitle2" fontWeight={700} mb={1}>{title}</Typography>
        <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">No data</Typography>
        </Box>
      </Box>
    );
  }

  // Compute overall % for header
  const totalPbj = rawData.reduce((s, d) => s + d.pbj, 0);
  const totalMds = rawData.reduce((s, d) => s + d.mds, 0);
  const overallPct = totalMds > 0 ? ((totalPbj / totalMds) - 1) * 100 : 0;

  // Chart scaling
  const allValues = data.flatMap(d => [d.pbj, d.mds]);
  const maxVal = Math.max(...allValues) * 1.1;

  const svgWidth = 400;
  const svgHeight = height - 40;
  const padding = { left: 45, right: 8, top: 10, bottom: 24 };
  const plotWidth = svgWidth - padding.left - padding.right;
  const plotHeight = svgHeight - padding.top - padding.bottom;

  const scaleX = (i: number) => padding.left + (i / (data.length - 1 || 1)) * plotWidth;
  const scaleY = (v: number) => padding.top + plotHeight - (v / maxVal) * plotHeight;

  // Generate paths
  const mdsPath = data.map((d, i) => `${i === 0 ? 'M' : 'L'} ${scaleX(i)} ${scaleY(d.mds)}`).join(' ');
  const pbjPath = data.map((d, i) => `${i === 0 ? 'M' : 'L'} ${scaleX(i)} ${scaleY(d.pbj)}`).join(' ');

  // Gap fill segments — red when understaffed (pbj < mds), light blue when above
  // Each consecutive pair of points forms a trapezoid, with crossover interpolation
  const gapSegments: { points: string; over: boolean }[] = [];
  for (let i = 0; i < data.length - 1; i++) {
    const d0 = data[i], d1 = data[i + 1];
    const x0 = scaleX(i), x1 = scaleX(i + 1);
    const diff0 = d0.pbj - d0.mds, diff1 = d1.pbj - d1.mds;

    if ((diff0 >= 0) === (diff1 >= 0) || diff0 === 0 || diff1 === 0) {
      // Same side — single trapezoid
      const pts = `${x0},${scaleY(d0.pbj)} ${x1},${scaleY(d1.pbj)} ${x1},${scaleY(d1.mds)} ${x0},${scaleY(d0.mds)}`;
      gapSegments.push({ points: pts, over: diff0 >= 0 });
    } else {
      // Lines cross — split at intersection
      const t = diff0 / (diff0 - diff1);
      const cx = x0 + t * (x1 - x0);
      const cy = scaleY(d0.mds + t * (d1.mds - d0.mds));
      // First half
      gapSegments.push({
        points: `${x0},${scaleY(d0.pbj)} ${cx},${cy} ${x0},${scaleY(d0.mds)}`,
        over: diff0 >= 0,
      });
      // Second half
      gapSegments.push({
        points: `${cx},${cy} ${x1},${scaleY(d1.pbj)} ${x1},${scaleY(d1.mds)}`,
        over: diff1 >= 0,
      });
    }
  }

  // Month labels on x-axis — show first week of each month, skip prior-year spillover
  const monthMarkers: { x: number; label: string }[] = [];
  let lastMonth = -1;
  data.forEach((d, i) => {
    if (!d.week) return;
    const monthNum = parseInt(d.week.split('-')[1]);
    if (monthNum !== lastMonth) {
      // Skip Dec at the start (week truncation can spill into prior year)
      if (i === 0 && monthNum === 12) { lastMonth = monthNum; return; }
      monthMarkers.push({ x: scaleX(i), label: MONTH_LABELS[monthNum - 1] });
      lastMonth = monthNum;
    }
  });

  return (
    <Box sx={{ flex: 1, minWidth: 240 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="baseline" mb={0.5}>
        <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
        <Typography
          variant="body2"
          fontWeight={700}
          sx={{ color: overallPct < 0 ? '#d32f2f' : '#2e7d32' }}
        >
          {overallPct >= 0 ? '+' : ''}{overallPct.toFixed(1)}%
        </Typography>
      </Box>

      {/* Chart */}
      <Box sx={{ position: 'relative', backgroundColor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200', opacity: loading ? 0.4 : 1, transition: 'opacity 0.2s' }}>
        {loading && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        <svg
          width="100%"
          height={svgHeight}
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
          preserveAspectRatio="xMidYMid meet"
        >
          {/* Y-axis grid lines and labels */}
          {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => {
            const val = maxVal * pct;
            const y = scaleY(val);
            return (
              <g key={i}>
                <line x1={padding.left} y1={y} x2={svgWidth - padding.right} y2={y} stroke="#e0e0e0" strokeWidth={1} />
                <text x={padding.left - 6} y={y} textAnchor="end" alignmentBaseline="middle" fontSize={10} fill="#999">
                  {val >= 1000 ? `${(val / 1000).toFixed(1)}k` : val.toFixed(0)}
                </text>
              </g>
            );
          })}

          {/* Gap fill — red when understaffed, light blue when above */}
          {gapSegments.map((seg, i) => (
            <polygon
              key={i}
              points={seg.points}
              fill={seg.over ? '#bbdefb' : '#ffcdd2'}
              fillOpacity={0.4}
            />
          ))}

          {/* MDS expected line (what should've been) */}
          <path d={mdsPath} fill="none" stroke="#1976d2" strokeWidth={2.5} strokeDasharray="6,3" />

          {/* PBJ actual line */}
          <path d={pbjPath} fill="none" stroke="#d32f2f" strokeWidth={2.5} />

          {/* Hover areas */}
          {data.map((d, i) => {
            const x = scaleX(i);
            const pbjVal = d.pbj ?? 0;
            const mdsVal = d.mds ?? 0;
            const pct = mdsVal > 0 ? (pbjVal / mdsVal) * 100 : 0;
            return (
              <Tooltip
                key={i}
                title={
                  <Box sx={{ fontFamily: 'monospace', fontSize: '12px' }}>
                    <Box sx={{ fontWeight: 600, mb: 0.5 }}>Week of {formatWeekLabel(d.week)}</Box>
                    {d.days < 7 && <Box sx={{ mb: 0.5, opacity: 0.8 }}>{d.days} days, scaled to a full week</Box>}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                      <span style={{ color: '#ef9a9a' }}>Actual:</span>
                      <span>{(d.pbj ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} hrs</span>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                      <span style={{ color: '#90caf9' }}>Required:</span>
                      <span>{(d.mds ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} hrs</span>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mt: 0.5, fontWeight: 600, color: pct < 100 ? '#ef9a9a' : '#a5d6a7' }}>
                      <span>Gap:</span>
                      <span>{pct < 100 ? '' : '+'}{(pct - 100).toFixed(1)}%</span>
                    </Box>
                  </Box>
                }
                arrow
              >
                <g>
                  {/* Hover line */}
                  {hoveredIndex === i && (
                    <line x1={x} y1={padding.top} x2={x} y2={svgHeight - padding.bottom} stroke="#999" strokeWidth={1} strokeDasharray="3,2" />
                  )}
                  {/* PBJ dot */}
                  <circle cx={x} cy={scaleY(d.pbj)} r={hoveredIndex === i ? 4 : 0} fill="#d32f2f" />
                  {/* MDS dot */}
                  <circle cx={x} cy={scaleY(d.mds)} r={hoveredIndex === i ? 4 : 0} fill="#1976d2" />
                  {/* Invisible hover target */}
                  <rect
                    x={x - plotWidth / data.length / 2}
                    y={padding.top}
                    width={plotWidth / data.length}
                    height={plotHeight}
                    fill="transparent"
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={() => setHoveredIndex(i)}
                    onMouseLeave={() => setHoveredIndex(null)}
                  />
                </g>
              </Tooltip>
            );
          })}

          {/* Month labels on x-axis */}
          {monthMarkers.map((m, i) => (
            <text
              key={i}
              x={m.x}
              y={svgHeight - 6}
              textAnchor="start"
              fontSize={10}
              fill="#999"
            >
              {m.label}
            </text>
          ))}
        </svg>
      </Box>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

const AcuityGapCharts: React.FC<{
  ccn: string;
  model?: 'anticipated' | 'hybrid';
  availableYears?: number[];
}> = ({ ccn, model = 'anticipated', availableYears }) => {
  const years = availableYears?.length ? availableYears : [2020, 2021, 2022, 2023, 2024];
  const [year, setYear] = useState(years[years.length - 1]);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<AcuityTimelineResponse | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/facilities/${ccn}/acuity-timeline/`, {
        params: { year, model },
      });
      setData(res.data);
    } catch (err) {
      console.error('Error fetching acuity timeline:', err);
    } finally {
      setLoading(false);
    }
  }, [ccn, year, model]);

  useEffect(() => {
    if (ccn) fetchData();
  }, [ccn, fetchData]);

  // Transform data for each staff type
  const rnData = data?.timeline.map(m => ({ week: m.week, pbj: m.pbj_rn, mds: m.mds_rn, days: m.days })) || [];
  const lpnData = data?.timeline.map(m => ({ week: m.week, pbj: m.pbj_lpn, mds: m.mds_lpn, days: m.days })) || [];
  const cnaData = data?.timeline.map(m => ({ week: m.week, pbj: m.pbj_cna, mds: m.mds_cna, days: m.days })) || [];

  return (
    <Box
      sx={{
        px: 1.5,
        py: 2.5,
        backgroundColor: 'white',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant="overline" color="text.secondary" fontWeight={600}>
            Acuity Gap
          </Typography>
          <Typography variant="h6" fontWeight={700}>
            Actual vs Required Staffing Hours
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Weekly PBJ hours staffed vs MDS acuity-required hours ({model === 'hybrid' ? 'Hybrid Model' : 'Anticipated Model'})
          </Typography>
        </Box>

        {/* Year selector */}
        <ToggleButtonGroup
          value={year}
          exclusive
          onChange={(_e, val) => { if (val !== null) setYear(val); }}
          size="small"
        >
          {years.map(y => (
            <ToggleButton key={y} value={y} sx={{ px: 1.5, py: 0.5, fontSize: '0.8rem' }}>
              {y}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {/* Charts */}
      <Box display="flex" gap={1.5} flexWrap="wrap">
        <StaffTypeChart title="Registered Nurse (RN)" data={rnData} loading={loading} />
        <StaffTypeChart title="Licensed Practical Nurse (LPN)" data={lpnData} loading={loading} />
        <StaffTypeChart title="Certified Nursing Assistant (CNA)" data={cnaData} loading={loading} />
      </Box>

      {/* Legend */}
      <Box display="flex" gap={3} justifyContent="center" mt={2}>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Box sx={{ width: 20, height: 3, backgroundColor: '#d32f2f', borderRadius: 1 }} />
          <Typography variant="caption" color="text.secondary">Actual (PBJ)</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Box sx={{ width: 20, height: 0, borderTop: '3px dashed #1976d2' }} />
          <Typography variant="caption" color="text.secondary">Required (MDS Acuity)</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Box sx={{ width: 14, height: 14, backgroundColor: '#ffcdd2', opacity: 0.6, borderRadius: 0.5 }} />
          <Typography variant="caption" color="text.secondary">Below Required</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Box sx={{ width: 14, height: 14, backgroundColor: '#bbdefb', opacity: 0.6, borderRadius: 0.5 }} />
          <Typography variant="caption" color="text.secondary">Above Required</Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default AcuityGapCharts;
